/**
 * Shapes for the WAPI prepaid wallet (RET-1763 scaffolding).
 *
 * CONTRACT CLOSED (RET-1684, 2026-08-05) against wallet-service#20. Balance and
 * movements are live; top-up options are not (RET-1757). Everything reaches
 * this repo through the BFF, never from wallet-service directly.
 *
 * Money rule (SSOT §7): the backend sends the amount already CONVERTED to the
 * workspace's currency, and the front never multiplies by the FX rate — this
 * repo already carries one instance of that mistake (`2500 * 21` in
 * ManageWatermark.modal.tsx). Formatting IS done here, which is the documented
 * exception; the rounding contract lives in Wallet.money.ts.
 */

/** Display currency, resolved by the backend from the subscription. */
export type tWalletCurrency = 'USD' | 'MXN';

export const WALLET_CURRENCIES: tWalletCurrency[] = ['USD', 'MXN'];

/**
 * De dónde sale la recarga automática vigente (RET-2259 · R2).
 *
 * - `wallet`: la recarga que el cliente configuró en la wallet.
 * - `credit_package`: el paquete de créditos recurrente convertido a saldo
 *   (RET-1711). Se cobra cada ciclo y se apaga desde el mismo switch, pero no
 *   se reemplaza desde el modal de recarga.
 *
 * El back puede omitir el campo (respuestas anteriores a RET-2259). Ausente, o
 * cualquier valor que no sea `credit_package`, se lee como `wallet`: es el
 * comportamiento que el front tenía antes del campo.
 */
export type tWalletRecurringSource = 'wallet' | 'credit_package';

/**
 * The wallet DERIVES its currency from its own ledger — the first movement
 * stamps it, from the subscription (RET-1685). A wallet that has never moved
 * money therefore has no derivable currency, and the backend answers `""`
 * rather than inventing one or keeping a second copy of it.
 *
 * Empty is valid, and only in that case: `currency: ""` always comes with
 * `amountMicros: "0"` and no rows. The front falls back to the subscription's
 * currency (`resolveWalletCurrency`) — it is the same source the wallet would
 * have stamped anyway.
 */
export type tWalletCurrencyOrEmpty = tWalletCurrency | '';

export type tWalletBalance = {
  currency: tWalletCurrencyOrEmpty;
  /**
   * Human-readable amount in `currency`, already converted and rounded by the
   * backend. Render this as-is; do not parse it to do arithmetic.
   */
  amount: string;
  /**
   * Exact integer micro-units as a string (never a JS number — 2^53 and float
   * drift both lose money). Use for comparisons, never for display.
   */
  amountMicros: string;
  /**
   * Low-balance state (RET-1760). `triggered` is decided by the backend: the
   * threshold formula (max of a week of recent spend and a US$5 / MXN$120
   * floor) and the fire-once-per-crossing state both live there. The front
   * reads the boolean and paints — recomputing it here would create a second
   * reader of the same rule.
   *
   * OPTIONAL because the balance endpoint shipped (RET-1756) BEFORE the
   * threshold did: today's payload carries no `lowBalance` at all. Absent means
   * "no warning", which is the only honest reading — synthesising a `false`
   * anywhere in the chain would make an unbuilt rule look like a rule that ran
   * and said no.
   */
  lowBalance?: tWalletLowBalance;
  /**
   * Lo que las reservas en curso tienen apartado (RET-1913, RET-2299).
   * `amount` ya es el saldo DISPONIBLE, neto de esto: el front no suma ni
   * resta. Existe para EXPLICAR una baja de saldo, porque una reserva no es un
   * movimiento y no aparece en la actividad.
   *
   * OPCIONAL por el orden de despliegue: el BFF lo reenvía desde bff-web#1055,
   * y un front que salga antes recibe el saldo sin la clave. El parser también
   * lo descarta si llega mal formado, en vez de rechazar el saldo entero.
   *
   * `amountMicros` puede valer "" (el BFF no recibió el campo) además de "0"
   * (nada apartado). `BigInt("")` es `0n`, así que nunca se convierte sin
   * pasar antes por `hasPositiveMicros`.
   */
  reserved?: tWalletReserved;
};

/** Monto apartado por reservas, con la misma pareja de strings que el saldo. */
export type tWalletReserved = {
  amount: string;
  amountMicros: string;
};

/**
 * Veredicto de saldo bajo, con la forma REAL que manda el BFF.
 *
 * Este tipo declaraba `threshold: string`, un campo que el BFF nunca envía, y
 * le faltaban los tres que sí manda. O sea que `threshold` era `undefined`
 * siempre y `dismissed` no existía para el front — por eso el banner reaparecía
 * después de descartarlo.
 *
 * Ningún campo lleva `?`, y es a propósito: el BFF los manda TODOS siempre,
 * incluso en false o cero, justamente para que el front no tenga que distinguir
 * "ausente" de "apagado". Un `??` sobre un campo que desaparece inventa un
 * default.
 */
export type tWalletLowBalance = {
  /** El hecho de DINERO: el saldo cayó bajo el umbral. */
  triggered: boolean;
  /**
   * El hecho de INTERFAZ: el cliente ya cerró el aviso de este episodio. Viaja
   * SEPARADO de `triggered` para que el back no tenga que mentir sobre el saldo
   * para acomodar una preferencia de UI. El banner se pinta con
   * `triggered && !dismissed`.
   */
  dismissed: boolean;
  /** Umbral en decimal exacto de 6 posiciones. */
  thresholdAmount: string;
  /** El mismo umbral como entero exacto en micros, como string. */
  thresholdAmountMicros: string;
  /**
   * Por qué el saldo puede quedarse bajo (RET-2422, RET-2427 · DS-16).
   * `auto_topup_paused`: la recarga por saldo bajo está pausada, y la tarjeta
   * de saldo ya lo explica, así que el banner no se pinta. Cualquier otro
   * valor, o ninguno, deja la regla `triggered && !dismissed`.
   *
   * La única excepción a "ningún campo lleva `?`": un payload anterior a
   * RET-2422 (caché, BFF viejo) no lo trae, y ausente debe leerse como hoy.
   */
  cause?: tWalletLowBalanceCause;
};

/** Valores que manda wallet-service hoy; el front sólo actúa sobre la pausa. */
export type tWalletLowBalanceCause = 'auto_topup_paused' | 'no_auto_topup';

export type tWalletBalanceResponse = {
  code: number;
  success: boolean;
  data: tWalletBalance;
};

/**
 * Why a discriminated status instead of `balance: tWalletBalance | null`
 * (review finding #5):
 *
 * A bare null collapses four different situations — the wallet does not apply
 * to this workspace, we do not know yet whether it applies, the request is in
 * flight, and the request failed. The R2/R3 send gate has to block sending
 * when the balance runs out, and it can only fail closed if it can tell
 * "this workspace has no money" apart from "I could not read the money".
 *
 * - `unknown`  -> feature flags still loading; the wallet may or may not apply
 * - `disabled` -> no workspace, or the flag is off for this one
 * - `loading`  -> flag on, request in flight
 * - `ready`    -> `balance` is authoritative for the current workspace
 * - `error`    -> the read failed; NEVER treat as "no money"
 */
export type tWalletStatus =
  'unknown' | 'disabled' | 'loading' | 'ready' | 'error';

/**
 * Ledger categories the movements table can show. The backend sends the
 * category, not a sentence: the label is translated here so the endpoint does
 * not have to know the customer's language.
 *
 * Mirrors the ledger categories agreed in RET-1684. `consumption` is the
 * aggregated template spend for a day; the rest arrive as their own rows.
 */
export type tWalletMovementCategory =
  | 'topup_one_time'
  | 'topup_recurring'
  /**
   * Abono del paquete recurrente de créditos migrado a saldo (RET-1711, U1/U7).
   * El renglón de Stripe sigue siendo el mismo del paquete de créditos; lo
   * único que cambia es que cada factura pagada entrega saldo en vez de
   * créditos. Categoría propia porque `topup_recurring` describe una recarga
   * configurada en la wallet, no una migrada desde otro producto (R4).
   */
  | 'topup_credit_package'
  | 'included_plan'
  | 'consumption'
  | 'courtesy'
  | 'migration'
  /**
   * Devolución de un consumo que no debió cobrarse (RET-1969): la inversa exacta
   * de `consumption`, con la que el wallet-service compensa un cobro duplicado
   * porque su ledger es append-only y el cobro original no se puede borrar.
   * No es `courtesy` —eso es un regalo; esto es dinero que nunca debió salir— y
   * no abre el desglose del día: el desglose es de lo cobrado, no de lo devuelto.
   */
  | 'consumption_reversal'
  /**
   * Recargas automáticas por saldo bajo de un día (RET-2421, RET-2427). Un solo
   * renglón por día con su `count`, que el texto muestra: el cliente cuadra ese
   * número contra los cargos de su estado de cuenta (DS-09).
   */
  | 'topup_threshold';

export type tWalletMovement = {
  /**
   * Stable row key, composed server-side as `<date>:<category>` — unique
   * because that pair is exactly what the backend groups by. Taken from the
   * server rather than built here so the key does not silently depend on the
   * shape of someone else's GROUP BY.
   */
  id: string;
  /**
   * Civil date (YYYY-MM-DD). The backend decided which day this belongs to, in
   * a single fixed timezone; the month name is formatted here with i18n. See
   * `formatMovementDate` for why this must never go through `new Date(iso)`.
   */
  date: string;
  category: tWalletMovementCategory;
  /** How many movements were summed into this row — "2 recargas". */
  count: number;
  /**
   * Exact 6-decimal amount with sign, as sent. Kept for debugging and for
   * anyone who needs the unrounded value; display goes through
   * `formatWalletAmount`, which rounds from `amountMicros`.
   */
  amount: string;
  /** Exact integer micro-units as a string. The display source of truth. */
  amountMicros: string;
  /** Drives the colour. Sent explicitly rather than parsed off `amount`. */
  isCredit: boolean;
};

export type tWalletMovementsPage = {
  movements: tWalletMovement[];
  page: number;
  totalPages: number;
  /** Same rule as the balance: empty when the wallet has no movements. */
  currency: tWalletCurrencyOrEmpty;
};

/**
 * Un renglón del desglose de consumo (RET-2077).
 *
 * `charged` y `reason` son campos propios y NO se deducen del importe: un renglón
 * gratis vale cero, así que deducir "fue gratis" de un cero ataría el significado al
 * valor, y el día que algo se tarife en cero la tabla lo etiquetaría mal.
 *
 * `country` es el ISO2 que resolvió la wallet. `'ZZ'` es el renglón «Otro» (CO-05):
 * un número que Meta no pricea, nunca un país inventado.
 */
/**
 * Las categorías de Meta que conocemos, y la ÚNICA lista de ellas en este front.
 *
 * La fuente de verdad vive en el CHECK de `price_catalog` de wallet-service, que es
 * quien tarifa. Esta copia existe para una sola cosa: distinguir «Meta mandó una
 * categoría que no conocemos» de «este asiento es viejo y no trae ninguna». Sin esa
 * distinción las dos caen en la misma etiqueta de «desconocido» y nadie se entera
 * nunca — que es exactamente cómo un contrato roto sobrevive tres semanas
 * (RET-2235), y el mismo agujero que `reportUnexplainedTierCounter` ya tapa para el
 * contador del cupo.
 *
 * No puede desincronizarse del copy en silencio: hay una prueba que exige que los
 * tres locales tengan una etiqueta por cada entrada de esta lista.
 */
export const WALLET_MESSAGE_CATEGORIES = [
  'marketing',
  'utility',
  'authentication',
  'authentication_international',
  'service',
] as const;

export type tConsumptionRow = {
  country: string;
  charged: boolean;
  /** El pricing_type crudo de Meta. Vacío en los renglones cobrados. */
  reason?: string;
  /**
   * La categoría de Meta con la que se tarifó el renglón (RET-2241): 'marketing' |
   * 'utility' | 'authentication' | 'authentication_international' | 'service'
   * (CINCO, no cuatro — el CHECK de price_catalog en wallet-service es la fuente
   * de verdad), en minúsculas, tal como la escribe el motor. Viaja en las DOS
   * mitades: CO-01 no distingue cobrado de gratis, así que un renglón GRATIS la
   * trae junto con su `reason` — son dos datos, ninguno desplaza al otro. Ausente
   * en los asientos viejos que el motor no la traía; en ese caso la columna de
   * tipo usa SU PROPIA clave de «desconocido», no la del país «Otro».
   */
  category?: string;
  messages: number;
  amount: string;
  amountMicros: string;
};

/**
 * El contador del cupo de Meta.
 *
 * ⚠️ `total` es `number | null`, y el null NO es un descuido del contrato: los dos
 * servicios de abajo se toman el trabajo de mandar null —no 0— cuando no pueden
 * afirmarlo, porque un cero AFIRMA que el cupo existe y va en cero. Tratarlo como
 * número aquí pintaría "0 de 1,000" sobre un dato que nadie tiene.
 */
export type tFreeTierCounter = {
  available: boolean;
  month?: string;
  unavailableReason?: string;
  total: number | null;
  /**
   * El TOPE de la franja (RET-2233 → RET-2283). Lo manda el backend —leído de la
   * tabla de reglas de whatsapp-business-service— y viaja SIN `omitempty` en las
   * dos vistas río arriba, así que la clave siempre existe. Hereda ENTERA la
   * disciplina de `total`: `null` es «no sé cuál es el tope», y un cero AFIRMA
   * que la franja existe y mide cero. Sin tope no hay restantes que calcular, y
   * eso NO es «cero restantes». Ningún consumidor lo suple con una constante
   * propia: la que este front tenía se borra en RET-2283, y con ella cualquier
   * default. Contrato congelado por la vara de RET-2283.
   */
  cap: number | null;
};

/** El detalle gratis de un mes: la puerta que abre el contador. */
export type tFreeMonthDetail = {
  workspace: string;
  month: string;
  timezone: string;
  rows: tConsumptionRow[];
  /**
   * Si el detalle pudo cargarse.
   *
   * De esta bandera cuelga el aviso de "no se pudo cargar, intenta más tarde", y no
   * es cosmética: hay workspaces cuyo mes entero son mensajes gratis, así que sin
   * ella "falló la carga" y "no mandaste nada" son la misma tabla vacía — y le
   * diríamos a alguien que mandó miles de mensajes que no mandó ninguno.
   */
  available: boolean;
  tierCounter: tFreeTierCounter;
};

export type tFreeMonthDetailResponse = {
  code: number;
  success: boolean;
  data: tFreeMonthDetail;
};

/**
 * El detalle de UN día: la puerta que abre la fila «Consumo de plantillas» de la
 * actividad (RET-2231, RET-2076).
 *
 * A diferencia de `tFreeMonthDetail`, ésta SÍ trae `currency` — la necesita el pie
 * de la tabla para formatear el total sumado, cosa que el mes nunca hace (su total
 * sale del primer renglón gratis, siempre cero).
 *
 * `rows` viene con TODO el día, cobrado y gratis: quien abre este detalle vino a
 * ver qué le costó, y filtrar los cobrados —como sí hace el detalle del mes—
 * dejaría una tabla de puros ceros.
 */
export type tDayDetail = {
  workspace: string;
  day: string;
  timezone: string;
  currency: tWalletCurrency;
  rows: tConsumptionRow[];
  /**
   * Si la mitad gratis del día pudo cargarse. Igual que en el mes, hay workspaces
   * cuyo día entero son mensajes gratis: sin esta bandera "no cargó" y "no
   * mandaste nada" son la misma tabla, y wallet-service sigue adelante con lo
   * cobrado cuando la mitad gratis no responde (consumption_breakdown.go), así
   * que el dinero nunca debe esconderse por esto.
   */
  freeDetailAvailable: boolean;
  tierCounter: tFreeTierCounter;
};

export type tDayDetailResponse = {
  code: number;
  success: boolean;
  data: tDayDetail;
};

export type tWalletMovementsResponse = {
  code: number;
  success: boolean;
  data: tWalletMovementsPage;
};

/**
 * What the balance settlement wait reads off a top-up charge outcome: the
 * status, nothing else.
 *
 * Declared structurally here instead of importing `tTopUpCharge` from the
 * modal, so the provider does not depend on a component's types. The link is
 * still checked where the two meet — the provider's handler only type-checks
 * as the modal's `onCharged` while `tTopUpCharge` still carries a `status`.
 */
export type tWalletChargeOutcome = {
  status: string;
};

/**
 * Qué pasó en el modal de recarga (RET-1928).
 *
 * Vive aquí y no en `Wallet.outcome.ts` por la misma razón que
 * `tWalletChargeOutcome`: el provider no debe depender de los tipos de un
 * componente, y poner la unión en el módulo de la tabla creaba un ciclo
 * Wallet.types <-> Wallet.outcome. La tabla importa de aquí, no al revés.
 *
 * Sólo los desenlaces que HOY se producen. Agregar un kind que ningún camino
 * dispara reconstruiría el defecto que este ticket cierra: un aviso declarado y
 * sin llamador, que es lo que fueron `onCharged` y el `refetch` de
 * `useAutoTopUp` durante meses.
 *
 * `dismissed` NO es un kind, y esa ausencia es deliberada. Cerrar la ventana
 * sin hacer nada corre `onClose` y no corre esto; la distinción vive en cuál
 * gancho se dispara, no en un centinela que cada consumidor tendría que
 * acordarse de filtrar.
 */
export type tTopUpOutcome =
  | {
      kind: 'charged';
      /**
       * Tal como lo devolvió el cable. Tipado con la forma estrecha que la
       * espera lee, no con `tTopUpCharge` del modal — el enlace se sigue
       * comprobando donde ambos se encuentran.
       */
      charge: tWalletChargeOutcome;
      /**
       * Micro-unidades exactas de lo que se cobró, como string (RET-1925).
       *
       * OBLIGATORIO, y por eso el compilador impide construir un `charged` sin
       * él: es lo que el candado de la segunda recarga compara, y un candado
       * que no puede comparar no es un candado. La respuesta del cobro NO lo
       * trae —`tTopUpCharge` es `{providerRef, status, credited}`— así que el
       * modal lo toma del MISMO valor que acaba de mandar al endpoint, nunca de
       * una segunda derivación que podría diferir de lo cobrado.
       *
       * String y nunca número: los micros pasan de 2^53 y el float pierde
       * dinero en silencio. Es la convención de toda la rebanada.
       */
      amountMicros: string;
      /**
       * RET-2426 (U6): lo que la respuesta de la recarga única dijo de la
       * recarga por saldo bajo, tal cual (`thresholdTopUp`: null o
       * `{resumed}`). No nulo significa que había una pausa y esta recarga la
       * tocó: la tarjeta tiene que releer. Ausente es null (los llamadores
       * anteriores a RET-2426 no lo traen).
       */
      thresholdTopUp?: { resumed: boolean } | null;
      /**
       * RET-2426 (KTD9): contra qué confirma la relectura posterior al pago
       * que hubo un cobro NUEVO de la automática. Va aparte de
       * `thresholdTopUp` porque ese es el cable y esto es lo que sabía el
       * modal; solo se lee cuando `thresholdTopUp.resumed` es true.
       *
       * `workspaceId`: el workspace donde se pagó. Si el de la pantalla ya es
       * otro, la relectura no se hace ni se pinta. `chargesBefore`: el
       * `today.count` que el modal leyó antes de pagar, o null si no lo tenía;
       * con null solo un `chargeInProgress` confirma el cobro.
       */
      resumption?: { workspaceId: string; chargesBefore: number | null } | null;
    }
  | {
      kind: 'recurring-enabled';
      /**
       * La fecha YA FORMATEADA por el modal, no el instante crudo.
       *
       * Es el mismo valor que el cliente acaba de leer en el resumen, así que
       * el aviso y el modal no pueden discrepar. Un segundo formateo aquí sí
       * podría: `formatWalletDate` fuerza UTC y el formateador corto de la
       * tabla de actividad lee partes locales — ver la nota de RET-1930.
       */
      startDateLabel: string;
      cadenceLabel: string;
    }
  /*
   * RET-2425: the threshold top-up was saved (R18). Three kinds, one per
   * thing the notice can say, so the table never re-reads the state to decide.
   */
  | {
      /** Saved, nothing charged: it charges when the balance drops below. */
      kind: 'threshold-configured';
      /** The limit, already formatted. */
      thresholdLabel: string;
    }
  | {
      /**
       * Saved, and THIS save charged (RC-20). The balance settles the same
       * way a charged one-off does.
       */
      kind: 'threshold-charged';
      /** As the wire returned it (`charge.status`). */
      charge: tWalletChargeOutcome;
      /** Exact micro-units of what was charged, from the response's `charge`. */
      amountMicros: string;
      amountLabel: string;
    }
  | {
      /**
       * Saved, with a charge of this top-up already in progress (KTD7): this
       * save did not charge. The amount is the in-flight charge's.
       */
      kind: 'threshold-charge-in-progress';
      amountLabel: string;
    }
  | {
      /**
       * RET-2425 (R11): the recurring top-up replaced the threshold one. Same
       * labels as `recurring-enabled`; its own kind because the notice says
       * the first charge date (`autoTopUp.configuredNotice.recurring`).
       */
      kind: 'recurring-replaced-threshold';
      startDateLabel: string;
      cadenceLabel: string;
    }
  /*
   * RET-2426 (U4): las salidas de la pausa de saldo bajo que no piden tarjeta
   * nueva. Ninguno de estos kinds lleva el `clientSecret` del 3DS (KTD8): el
   * secreto vive solo en la variable local del handler del reintento.
   */
  | {
      /**
       * «Reintentar el cobro» cobró (`charged`) o el banco aún confirma
       * (`pending`). También el 3DS: `charged` cuando la relectura muestra la
       * pausa terminada y `pending` cuando las relecturas se agotan.
       */
      kind: 'threshold-retry-charged';
      charge: tWalletChargeOutcome;
      /** Micro-unidades exactas del cobro, del `charge.amount` de la respuesta. */
      amountMicros: string;
    }
  | {
      /** El reintento terminó sin cobro, por un motivo que se le dice al cliente. */
      kind: 'threshold-retry-rejected';
      reason: tThresholdRetryRejection;
    }
  | {
      /**
       * 424, 5xx, red o un error sin clasificar: no se sabe si hubo cobro. El
       * kind se arma DESPUÉS de releer el GET, y `pause` es lo que esa
       * relectura mostró: `still` (sigue en pausa), `ended` (ya no) o `unknown`
       * (la relectura también falló).
       */
      kind: 'threshold-retry-unconfirmed';
      pause: 'still' | 'ended' | 'unknown';
    }
  | {
      /** Se apagó la recarga por saldo bajo (`DELETE …/topup/threshold`, R9). */
      kind: 'threshold-disabled';
    }
  | {
      /**
       * RET-2426 (U6, KTD9): una recarga única pagada con la tarjeta de la
       * pausa la terminó. Se arma DESPUÉS de releer el GET: `charged` es true
       * solo si esa relectura mostró un cobro nuevo de la automática (un
       * `chargeInProgress`, o un `today.count` mayor que el de antes de pagar).
       * Nunca sale de la predicción del pie.
       */
      kind: 'threshold-resumed';
      charged: boolean;
      /** El monto de la automática, ya formateado; '' cuando la lectura no lo trajo. */
      amountLabel: string;
    };

/**
 * Por qué un «Reintentar el cobro» terminó sin cobro (RET-2426, U4).
 *
 * - `declined`: 402, el banco rechazó otra vez; la pausa muestra el motivo nuevo.
 * - `authentication_failed`: `handleNextAction` devolvió error o el cliente
 *   abandonó el 3DS.
 * - `authentication_unavailable`: `requires_action` sin `clientSecret`, o sin
 *   Stripe.js cargado; no hay cómo abrir el 3DS.
 * - Los siete del 409 del contrato (A.5), uno por uno.
 * - `rejected`: el 400 (tarjeta no adjunta, cuerpo o llave) y el 409 sin
 *   `reason`. El front no los distingue: wallet no manda `reason` en ellos.
 */
export type tThresholdRetryRejection =
  | 'declined'
  | 'authentication_failed'
  | 'authentication_unavailable'
  | 'not_paused'
  | 'daily_cap'
  | 'charge_in_progress'
  | 'not_retryable'
  | 'frozen'
  | 'no_wapi'
  | 'multiple_subscriptions'
  | 'rejected';

/**
 * En qué punto va la acreditación de una recarga (RET-1925).
 *
 * POR QUÉ EXISTE. La espera acotada de RET-1870 era completamente INTERNA: su
 * contador de generación es un ref privado y el contexto no exponía nada de
 * ella. Sin esto la tarjeta de saldo se ve idéntica a la de no haber hecho nada
 * durante los segundos en que la acreditación viaja por webhook, ledger,
 * outbox, RabbitMQ, websocket y reconsulta — y quien cree que su recarga no
 * entró, recarga otra vez. La segunda cobra de verdad, porque el modal descarta
 * la llave del intento al recibir el 200.
 *
 * TRES formas y no un booleano `isSettling`, y la razón es `exhausted`: tiene
 * que SOBREVIVIR al fin de la espera, porque es lo único que le permite a la
 * pantalla decir que la acreditación tarda más de lo normal. Un booleano vuelve
 * a false cuando la espera termina, y ese estado se pierde justo cuando es el
 * único que hay que contar.
 *
 * Ninguna forma lleva una cifra de saldo, y eso es la restricción de ADR-013
 * hecha tipo: el estado intermedio se narra con palabras, nunca inventando el
 * número. El único monto que viaja es el que se COBRÓ, que es un hecho
 * confirmado, y viaja sólo para que el candado de la segunda recarga compare.
 */
export type tWalletSettlement =
  | { kind: 'idle' }
  | {
      /** El cobro pasó; la acreditación viene en camino. */
      kind: 'settling';
      /**
       * Micro-unidades exactas del cobro que arrancó la espera, o null cuando
       * quien la arrancó no las dijo.
       *
       * `null` significa "no se puede comparar", nunca "no coincide": el
       * candado del mismo monto trata la ausencia como falta de coincidencia,
       * así que no muerde por accidente sobre un cobro cuyo monto se perdió.
       */
      amountMicros: string | null;
    }
  | {
      /** Se agotó el presupuesto de espera y el saldo no subió. */
      kind: 'exhausted';
      amountMicros: string | null;
    };

/**
 * Las tres salidas del contador de la franja, vistas desde el contexto
 * (RET-2319). Ver `freeTierCounterStatus` en `tWalletContext`.
 */
export type tFreeTierCounterStatus = 'ready' | 'waiting' | 'unknown';

export type tWalletContext = {
  status: tWalletStatus;
  /**
   * Non-null ONLY when status is `ready`, and only ever the current
   * workspace's balance — the provider stamps every fetch with the workspace
   * it belongs to and refuses to serve a value stamped with another one
   * (review finding #1: showing workspace A's money under workspace B).
   */
  balance: tWalletBalance | null;
  isLoadingBalance: boolean;
  balanceError: unknown;
  /**
   * Re-reads the balance for every surface at once and RESOLVES once the new
   * value is committed to state (or the failure is), so a caller that moved
   * money can await it before closing a modal or unblocking an action.
   * Resolves with null on failure — it never rejects; read `status` to tell a
   * failure apart from an empty wallet.
   */
  refetchBalance: () => Promise<tWalletBalance | null>;
  /**
   * Asegura que hay una lectura RECIENTE del saldo, uniéndose a la que ya esté
   * en vuelo en vez de emitir una por llamador (RET-2107).
   *
   * DOS ENTRADAS PORQUE SON DOS GARANTÍAS, no dos caminos para un trabajo, y
   * confundirlas rompe algo medido. `refetchBalance` promete «lee AHORA», y hay
   * dos consumidores que dependen de esa promesa literal: el botón de reintento
   * de la tarjeta, que existe justo para forzar una lectura nueva, y la espera
   * de acreditación, que compara lecturas SUCESIVAS para ver que el saldo subió.
   * Si a `refetchBalance` se le agregara coalescencia, dos ticks consecutivos de
   * esa espera se unirían a la misma lectura, compararían un número contra sí
   * mismo y quemarían un tick — el defecto de RET-1870 reconstruido desde el
   * otro lado. Quien necesite forzar una lectura llama a `refetchBalance`;
   * quien sólo necesite que el número de la pantalla no esté vencido llama aquí.
   *
   * No devuelve el saldo, y es deliberado: quien revalida no tiene ninguna
   * decisión que tomar con el valor — lo lee de `balance` cuando aterriza. No
   * rechaza nunca; un fallo se cuenta por `status`, igual que en
   * `refetchBalance`.
   *
   * Inerte sin workspace o con la bandera apagada, sin emitir tráfico.
   */
  revalidateBalance: () => Promise<void>;
  /**
   * Settles the balance after a top-up charge (RET-1870): re-reads it with
   * increasing gaps and stops as soon as it goes UP, the budget runs out, or a
   * read fails terminally. Why a wait is needed at all — a 200 means charged,
   * not credited — is stated once, on the wait itself in `Wallet.provider.tsx`.
   *
   * Only a real charge starts the wait. A 3DS `pending` gets exactly ONE
   * re-read and no wait: `pending` means not yet, not never, so a challenge the
   * customer completed before this ran is picked up, while nothing here can
   * learn when the bank answers later. Any other status starts nothing and is
   * reported to Sentry. A second charge supersedes the wait in progress; two
   * waits never run at once.
   *
   * Returns nothing on purpose. The caller has no decision left to make, and
   * awaiting 15 seconds inside a modal handler is not one of its jobs — every
   * surface reads the new balance off `balance` when it lands.
   */
  settleBalanceAfterCharge: (outcome: tWalletChargeOutcome) => void;
  /*
   * El contrato publicado sigue siendo de UN parámetro, y eso es deliberado
   * (RET-1925). El provider le pasa además el monto cobrado, pero sólo desde
   * `applyOutcome`, así que el monto llega a la espera únicamente por la tabla
   * de desenlaces. Publicarlo aquí abriría un segundo camino para la misma
   * regla —una superficie arrancando una espera con monto por su cuenta— y dos
   * caminos para una regla es exactamente cómo esto se rompió la primera vez.
   */
  /**
   * Aplica un desenlace del modal de recarga (RET-1928): consulta la tabla de
   * `Wallet.outcome.ts` y dispara lo que ésta declare.
   *
   * Es la ÚNICA traducción desenlace -> qué releer. Antes esa regla vivía
   * repartida en lo que cada autor recordó, y por eso el cierre del modal
   * releía el saldo aunque lo que hubiera cambiado fuera otra cosa.
   *
   * No pinta ni avisa: eso es del hook que lo envuelve. Aquí sólo se invalida.
   */
  applyOutcome: (outcome: tTopUpOutcome) => void;
  /**
   * Sube cada vez que un desenlace invalida la configuración de la recarga
   * automática. `useAutoTopUp` lo lee como dependencia de su efecto.
   *
   * Un CONTADOR y no una función imperativa, porque el hook que tiene que
   * releer vive dentro de `ConnectedBalanceCard`, que se monta en tres
   * lugares — un nivel por debajo de quien es dueño del cierre del modal. Un
   * token deja que cualquier superficie invalide sin saber quién escucha, y que
   * un sitio de montaje nuevo herede el comportamiento sin cablear nada. Es la
   * razón por la que el `refetch` que el hook exponía nunca tuvo llamador.
   */
  autoTopUpToken: number;
  /**
   * En qué punto va la acreditación, YA filtrado por el workspace en pantalla
   * (RET-1925) — misma estampa que el saldo, por la misma razón: una espera
   * arrancada en un workspace pintaría el estado intermedio en otro después de
   * un cambio de sidebar.
   *
   * A diferencia de `status`, aquí no hay forma `unknown`, y es deliberado. Un
   * consumidor fuera del provider recibe `idle`, que es lo único honesto: no
   * hay espera de la que pueda formar parte. Y no puede sesgar una decisión de
   * dinero, porque esto no decide nada de dinero — narra, y el candado que sí
   * frena un cobro vive dentro del provider.
   */
  settlement: tWalletSettlement;
  /**
   * El contador de la franja de Meta (RET-2283), izado al provider porque es
   * POR WORKSPACE (`RE-13`), no por conversación: una sola lectura la comparten
   * todos los chats abiertos, con refresco de 60 s mientras haya un chat WAPI
   * abierto y la cohorte de wallet encendida. Fuera de la cohorte no se hace
   * NINGUNA lectura para poblarlo.
   *
   * FÓRMULA (contrato congelado por la vara de RET-2283): la última lectura
   * COMPROMETIDA del contador para el workspace EN PANTALLA, o `null`. Es
   * `null` mientras la lectura carga, cuando falló, con la cohorte apagada o el
   * flag cargando, sin workspace y —por la misma estampa que `balance`— siempre
   * que lo guardado sea de OTRO workspace. Nunca un objeto inventado y nunca el
   * contador del workspace anterior.
   *
   * Lo leen DOS consumidores del compositor, con preguntas distintas. El sello
   * trata `null` como «no puedo afirmar»: ante la duda, «Con costo». El guardia
   * de saldo (`isBlocked`, `useComposerServiceSeal.ts`; RET-2079) lo lee para
   * soltar la barra «sin saldo» mientras la franja cubra el envío (`DS-18`,
   * `DS-19`, `DS-20`): la WBS autoriza el texto libre de servicio sin mirar el
   * saldo mientras quede franja, así que frenarlo en el front era el error. Lo
   * lee con su PROPIO predicado y sin el margen del sello (`freeTierRemaining`,
   * `Wallet.freeTier.ts`), y con `null` NO suelta: ante la duda bloquea, como
   * siempre (fail-closed).
   */
  freeTierCounter: tFreeTierCounter | null;
  /**
   * El instante de EMISIÓN de la lectura de saldo que `balance` está
   * sirviendo (RET-2327, C-1): el `Date.now()` que `performFetch` toma
   * INMEDIATAMENTE ANTES de llamar a `WalletService.getBalance`, para la
   * lectura que escribió el `ready` vigente.
   *
   * OBLIGATORIO (no `?`) a propósito: es la marca contra la que el sello del
   * compositor decide si el saldo local puede desmentir al servidor
   * (`isLocalBalanceFresher`, `useComposerServiceSeal.ts`), y un campo que
   * pudiera faltar dejaría esa comparación sin saber qué significa su
   * ausencia.
   *
   * EMISIÓN y no llegada: el orden que importa es el de las lecturas EN EL
   * SERVIDOR. La lectura del saldo ocurre después de emitir su petición; la
   * de la cotización, antes de que su respuesta llegara. Estampar la llegada
   * dejaría pasar como «más nueva» una petición de saldo emitida ANTES que la
   * cotización y llegada DESPUÉS.
   *
   * `null` si y sólo si `balance` es `null` — los dos salen de la MISMA
   * lectura servida: idle, loading, error, otro workspace, bandera apagada, y
   * en `DEFAULT_WALLET_CONTEXT`. Mientras la bandera se relee, el `ready`
   * vigente se sigue sirviendo, y su marca con él. Una
   * lectura que no se compromete (superada, de otro workspace, cancelada) no
   * toca la marca; una falla que escribe `error` la deja en `null`; la falla
   * de la espera de liquidación con `preserveVisibleBalanceOnFailure` no
   * toca el estado `ready` y por lo tanto conserva su marca.
   */
  balanceReadStartedAt: number | null;
  /**
   * RET-2319 · vuelta 3 · la ESTAMPA (`Date.now()`) de la lectura que viaja en
   * `freeTierCounter`, o `null` cuando no hay ninguna servible. La juzgan el
   * sello (para afirmar) y, desde RET-2079, el guardia (para soltar un
   * bloqueo): los dos con la misma `freeTierReadingIsStale`.
   *
   * Invariante: `freeTierCounterReadAt !== null` ⟺ `freeTierCounter !== null`
   * ⟺ `freeTierCounterStatus === 'ready'`.
   *
   * PARA QUÉ SIRVE, que no es obvio: quien AFIRMA sobre el contador tiene que
   * juzgar su vejez con el reloj de SU render, no confiar en que el provider ya
   * lo hizo. El provider también la juzga, pero hay un commit que no alcanza:
   * `Date.now()` no puede ser dependencia de un `useMemo`, y cuando el
   * compositor se remonta no cambia ninguna dep del memo del contexto, así que
   * ese render sirve el valor cacheado —con el contador rancio y
   * `status: 'ready'` dentro—. El sello se re-renderiza al montarse y ve el
   * reloj nuevo: con la estampa en la mano calla, en vez de prometer franja
   * sobre un contador muerto. Se juzga con `esEstampaFresca`
   * (`Wallet.freeTier.ts`), que es el dueño único de la cota.
   *
   * OBLIGATORIO a propósito, por el mismo motivo que
   * `freeTierCounterStatus`: un arnés que arme `tWalletContext` a mano debe
   * romper en compilación y declarar qué edad tiene la lectura que sirve.
   */
  freeTierCounterReadAt: number | null;
  /**
   * RET-2319 · LA pregunta que el sello le hace al contador, en tres salidas y
   * no en dos, porque `freeTierCounter: null` mezclaba cosas que merecen
   * respuestas OPUESTAS:
   *
   *   `ready`    hay contador FRESCO del workspace en pantalla ⇒ el sello
   *              decide: franja si la banda está abierta, «Con costo» si no.
   *   `waiting`  la respuesta viene en camino, o lo que hay es de otro
   *              workspace, o cruzó la cota de vejez ⇒ el sello CALLA. No
   *              afirma precio sin datos, que es el defecto de este ticket.
   *   `unknown`  se preguntó y la lectura cayó, o el consumidor está ARRIBA
   *              del provider y nunca va a aprender ⇒ el sello dice «Con
   *              costo». Callar para siempre esconde un costo real.
   *
   * OBLIGATORIO a propósito, a diferencia del `isLoadingFreeTierCounter` que
   * reemplaza: un arnés que arme `tWalletContext` a mano debe romper en
   * compilación en vez de heredar un `undefined` silencioso. Ese aviso es el
   * que queremos.
   *
   * El guardia de saldo lo lee TAMBIÉN (RET-2079), con otra lectura de las
   * mismas tres salidas: sólo `ready` (y fresca, y con `available`, `cap` y
   * `total` válidos) puede soltar la barra «sin saldo». `waiting` y `unknown`
   * NO sueltan nada, porque ante la duda el guardia protege dinero (`DS-18`,
   * fail-closed). A diferencia del sello, donde `waiting` calla y `unknown`
   * dice «Con costo», para el guardia las dos significan lo mismo: no sueltan.
   */
  freeTierCounterStatus: tFreeTierCounterStatus;
  /**
   * @deprecated RET-2319 lo reemplaza por `freeTierCounterStatus`, que parte
   * tres casos donde este booleano sólo partía dos. Se conserva derivado y sin
   * lectores en producción porque dos varas congeladas lo sirven a mano; se
   * retira cuando migren.
   *
   * `freeTierCounter` en `null` mezclaba dos cosas que merecen respuesta
   * OPUESTA (RET-2319): la respuesta VIENE (transitorio) o la lectura ya NO
   * VA A LLEGAR —cayó, la cohorte está apagada, no hay workspace— (durable).
   * Misma forma que `isLoadingBalance` al lado de `balance`, por la misma
   * razón: un solo booleano al lado del valor que puede estar `null`.
   *
   * `true` SÓLO mientras la lectura del contador para el workspace EN
   * PANTALLA está en vuelo y todavía no hay un contador comprometido para
   * ese workspace (la primera lectura, o la primera tras cambiar de
   * workspace). Un refresco del MISMO workspace con contador ya listo NO es
   * carga: el provider conserva el anterior mientras llega, así que esto se
   * queda en `false` y `freeTierCounter` no se vacía.
   *
   * `false` con contador listo, con la lectura caída, con la cohorte
   * apagada o el flag cargando, sin workspace, y en el DEFAULT de
   * arriba-del-provider — ahí es un unknown DURABLE («no puede saber»),
   * nunca una carga que algún día resuelve.
   *
   * Quien lo lee (el sello) distingue con esto «calla, la respuesta viene»
   * de «afirma Con costo, esto no se va a resolver solo». No decide dinero:
   * el guardia de saldo no lo lee (lee `freeTierCounterStatus`).
   *
   * OPCIONAL, y es deliberado: este campo nace en RET-2319, después de que
   * media docena de arneses de prueba ya construían un `tWalletContext`
   * completo a mano (sin tocar ESOS archivos, fuera del alcance de este
   * ticket). `undefined` se lee exactamente como `false` en cada
   * consumidor — el mismo «no sé, pero tampoco estoy cargando» que esos
   * arneses ya describían antes de que este campo existiera.
   */
  isLoadingFreeTierCounter?: boolean;
};

/**
 * Respuesta del descarte del aviso de saldo bajo (RET-1695).
 *
 * Trae el veredicto ENTERO, no un ack vacío: el banner se repinta con esto y no
 * con una segunda lectura, que podría contestar sobre un saldo distinto al que
 * el cliente acaba de ver.
 */
export type tLowBalanceDismissResponse = {
  code: number;
  success: boolean;
  data: { lowBalance: tWalletLowBalance };
};

/**
 * Cotización de costo de envío (RET-1806, DS-03/DS-12/UC-09).
 *
 * `POST /workspace/{wid}/wallet/estimate` — cotiza por CATEGORÍA de Meta
 * (marketing | utility | authentication | authentication_international |
 * service), nunca por template id. `recipients` son los handles a cotizar.
 *
 * CONTRATO CRÍTICO (bff-web/src/wallet/wallet.types.go): el discriminador de
 * «no se pudo cotizar» es `unresolvedRecipients`, NUNCA `total === 0` — un
 * total en 0 con destinatario resuelto es gratis en origen (ventana 24h /
 * free entry point, UC-09), no una cotización imposible.
 *
 * `currency` viaja como string abierto (no `tWalletCurrency`) porque este es
 * el contrato del estimador, no el del saldo — un lector que necesite
 * formatear valida contra `WALLET_CURRENCIES` antes de usarlo, igual que
 * `resolveWalletCurrency` hace para el saldo.
 */
export type tSendCostEstimate = {
  currency: string;
  recipientsQuoted: number;
  total: { amount: string; amountMicros: string };
  balance: { amount: string; amountMicros: string };
  balanceAfter: { amount: string; amountMicros: string };
  /** SIN omitempty en el BFF: siempre presente, aunque vacío. */
  unresolvedRecipients: string[];
};

/**
 * RET-2232 D-28: la última cotización CONOCIDA de `useComposerServiceSeal`
 * para un scope `${workspaceId}::${chatId}::${recipientHandle}`, guardada en
 * un ref que vive por ENCIMA de `TextAreaContainer` (`Chat.hook.ts`,
 * `composerSealQuoteRef`) — no en el estado de ese componente.
 *
 * Por qué hace falta un ref ahí y no basta el estado del hook: D-24 fijó que
 * el ÁMBITO del guardia es (workspace, conversación), no el ciclo de vida
 * del componente, pero sólo cerró la mitad de la fuga (dejó de soltarse al
 * DESMONTAR). `ChatEnum.WABTEMPLATES_VARIABLES` desmonta y vuelve a montar
 * `TextAreaContainer` sin cambiar `selectedChat` (D-24) — al remontar, el
 * estado LOCAL del hook (`useState`) nace de cero, así que la escritura del
 * MONTAJE es la liberación que faltaba cerrar (D-28). Un ref en un contexto
 * que no se desmonta con `TextAreaContainer` es lo único que sobrevive ese
 * viaje; `null` cuando no hay nada cacheado para el scope vigente.
 *
 * Ver `useComposerServiceSeal.ts`.
 *
 * `quotedAt` (RET-2327, C-2): el `Date.now()` de ADOPCIÓN de `estimate` (ver
 * C-4 en `useComposerServiceSeal.ts`), guardado junto con la cotización.
 * Restituir una cotización desde esta caché restituye SU marca — no la hora
 * del remontaje — para que D-28 y la válvula fechada de D-8 sigan de acuerdo
 * sobre cuándo se cotizó.
 */
export type tComposerSealQuoteScope = {
  scopeKey: string;
  estimate: tSendCostEstimate;
  quotedAt: number;
} | null;

export type tSendCostEstimateResponse = {
  code: number;
  success: boolean;
  data: tSendCostEstimate;
};

/**
 * El faltante para cubrir un envío, CON su moneda (RET-1806).
 *
 * La moneda viaja pegada al monto y no aparte porque el destino de este dato es
 * una COMPARACIÓN: `computeTopUpPrefillAmount` lo contrasta contra el piso de
 * RC-10 que llega en el payload de recarga (`options.minFreeAmountMicros`), y
 * `max(faltante, piso)` entre dos monedas distintas no da un monto, da un
 * número. Con la moneda dentro del mismo objeto no hay forma de perderla en el
 * camino, que es exactamente lo que pasó: el faltante cruzaba tres capas
 * (el hook del gate → useTopUpModal → el modal) como un string pelado.
 *
 * Hoy las dos monedas coinciden siempre porque wallet-service las deriva de la
 * misma suscripción, pero esa es una garantía ajena — y este repo ya tiene el
 * criterio escrito para el caso (Wallet.money.ts): «Callers must not substitute
 * a default — printing "US$0.00" for a workspace that bills in MXN is a wrong
 * number».
 */
export type tWalletShortfall = {
  /** Micro-unidades, entero positivo como string. */
  amountMicros: string;
  currency: tWalletCurrency;
};

/**
 * El recibo del corte de créditos a saldo (RET-2142): de qué bolsas salió el
 * dinero que el cliente recibió el día que sus créditos dejaron de existir.
 *
 * Lo sirve `GET /workspace/:wid/wallet/cutover`. Casi ningún workspace tuvo
 * créditos que convertir, así que `available: false` es la respuesta NORMAL y no
 * un fallo — quien la consuma distingue «no hubo corte» de «no se pudo leer» por
 * ahí, nunca por una lista vacía.
 */
export type tWalletCutoverLine = {
  /**
   * La fila de billing que produjo el renglón. No se pinta: es para auditar.
   * En un renglón recurrente (L-2/L-3) plegado por wallet-service es el id MÁS
   * BAJO de su grupo; L-4 reusa el de su bolsa recurrente, así que la llave de
   * React es `bucketId` + `rule`.
   */
  bucketId: number;
  /**
   * La regla de conversión CRUDA: `L-1` compras únicas · `L-2` recurrente con
   * paquete · `L-3` recurrente solo plan · `L-4` liquidación de meses prepagados.
   *
   * Llega sin traducir a propósito, y por eso la etiqueta se decide acá: el
   * catálogo de reglas cambia más seguido que este contrato, y una regla que no
   * reconozcamos tiene que seguir pintándose con sus créditos y su importe en vez
   * de desaparecer.
   */
  rule: string;
  /** `recurring` | `one_time`, o vacío en la liquidación, que no salió de bolsa. */
  kind: string;
  credits: number;
  /**
   * El ciclo que cubría la bolsa, `YYYY-MM-DD`, o **null** en la liquidación:
   * cotiza meses de un plazo pagado que nunca existieron como bolsa, así que no
   * hay ciclo que nombrar. null y no cadena vacía — ver el BFF.
   */
  periodStart: string | null;
  periodEnd: string | null;
  ratePerCredit: string;
  ratePerCreditMicros: string;
  /** Legible ya compuesto por el backend; para PINTAR va `amountMicros`. */
  amount: string;
  amountMicros: string;
};

export type tWalletCutoverReceipt = {
  available: boolean;
  currency: tWalletCurrencyOrEmpty;
  /**
   * 🔴 La suma de los renglones: lo que la conversión produjo AQUEL DÍA. **No es
   * el saldo de hoy.** Quien ya gastó parte de su saldo ve dos números distintos
   * en la misma pantalla, y los dos son correctos.
   */
  total: string;
  totalMicros: string;
  lines: tWalletCutoverLine[];
};

export type tWalletCutoverReceiptResponse = {
  code: number;
  success: boolean;
  data: tWalletCutoverReceipt;
};

// ── Recarga por saldo bajo (RET-2425) ───────────────────────────────────────
//
// Tipos del contrato de wallet-service (`threshold_topup_controller.go`,
// RET-2420), que el BFF relaya sin re-tipar (RET-2424, `thresholdTopUpResponse`).
// Cada nombre sale de una etiqueta `json:"…"` de ese controlador. Los campos que
// pueden venir en null van SIN omitempty del lado de Go: null y ausente
// significan cosas distintas, y el parser de la frontera (Wallet.service.ts)
// rechaza un campo ausente.

/**
 * `{amount, amountMicros}` de la recarga por saldo bajo (`thresholdAmountJSON`).
 *
 * A diferencia del resto del wallet, `amount` va en UNIDADES ENTERAS sin
 * decimales ("25", no "25.000000"): los montos de esta recarga son siempre
 * enteros (RC-18). `amountMicros` es el mismo monto como entero exacto en
 * string, y es el que se usa para comparar o calcular.
 */
export type tThresholdAmount = {
  amount: string;
  amountMicros: string;
};

/**
 * `status` de la configuración. `paused` si y solo si `pause` no es null
 * (RET-2421); solo `off` es «apagada».
 */
export type tThresholdTopUpStatus = 'active' | 'off' | 'paused';

export const THRESHOLD_TOPUP_STATUSES: readonly tThresholdTopUpStatus[] = [
  'active',
  'off',
  'paused',
];

/**
 * Por qué el SISTEMA apagó la recarga (`offReason`, RET-2423). Null cuando la
 * apagó una persona. Unión cerrada: un valor fuera de la lista no rompe la
 * lectura, el parser lo deja en null (ver `parseOffReason`).
 */
export type tThresholdOffReason =
  'downgrade' | 'pause' | 'exempt' | 'trial' | 'canceled' | 'dispute';

export const THRESHOLD_OFF_REASONS: readonly tThresholdOffReason[] = [
  'downgrade',
  'pause',
  'exempt',
  'trial',
  'canceled',
  'dispute',
];

/**
 * Por qué está en pausa (`pause.reason`, RET-2421). `daily_cap`: tope de cobros
 * automáticos del día. `not_completed`: el cobro no terminó. `declined`: el
 * banco lo rechazó.
 */
export type tThresholdPauseReason = 'daily_cap' | 'not_completed' | 'declined';

export const THRESHOLD_PAUSE_REASONS: readonly tThresholdPauseReason[] = [
  'daily_cap',
  'not_completed',
  'declined',
];

/** Marca y últimos 4 de la tarjeta de la pausa; nada más de la tarjeta. */
export type tThresholdPauseCard = {
  brand: string;
  last4: string;
};

/**
 * El `pause` del contrato (`thresholdPauseJSON`). Solo existe con
 * `status: paused`.
 *
 * - `until`: solo con `daily_cap` (00:00 del día siguiente en la zona del
 *   workspace, en UTC).
 * - `charge`: el intento que causó la pausa; null si no se pudo leer. En
 *   `daily_cap` es el monto que se cobrará mañana.
 * - `declineReason`: texto de contrato, conjunto abierto (un valor sin
 *   traducción llega tal cual). Null en `daily_cap`.
 * - `card`: null sin tarjeta, en `daily_cap`, con marca migrada o si Stripe no
 *   se pudo leer. La pantalla no puede suponerla.
 * - `oneOffResumes`: true solo si una recarga única pagada con la tarjeta
 *   predeterminada terminaría la pausa. El contrato lo tipa `*bool`; un null
 *   se lee como false, que es el lado que no promete nada.
 */
export type tThresholdPause = {
  reason: tThresholdPauseReason;
  /** RFC 3339 en UTC. */
  since: string;
  /** RFC 3339 en UTC. */
  until: string | null;
  charge: { amount: tThresholdAmount } | null;
  declineReason: string | null;
  retryableWithSameCard: boolean;
  card: tThresholdPauseCard | null;
  oneOffResumes: boolean;
};

/**
 * Por qué no se puede cambiar entre la frecuente y la de saldo bajo
 * (`typeChange.blockedBy`, texto de contrato). Conjunto abierto: un motivo que
 * este front no conoce llega tal cual y se pinta con el texto genérico.
 */
export type tThresholdBlockedBy = 'past_due' | 'credit_package_scheduled';

export type tThresholdTypeChange = {
  allowed: boolean;
  blockedBy: string | null;
  /** Fecha `YYYY-MM-DD` del cambio programado del paquete, si Stripe la da. */
  scheduledChangeOn: string | null;
};

export type tThresholdLimits = {
  thresholdMin: tThresholdAmount;
  thresholdDefault: tThresholdAmount;
  amountMin: tThresholdAmount;
  amountMax: tThresholdAmount;
  amountDefault: tThresholdAmount;
  /** Tope de cobros automáticos por día (RC-19). */
  dailyMax: number;
};

export type tThresholdLastValues = {
  threshold: tThresholdAmount;
  amount: tThresholdAmount;
};

/**
 * El cobro al programar que todavía no se acredita. Su monto es el del cobro
 * en curso, no el que el cliente pide ahora (KTD7).
 */
export type tThresholdChargeInProgress = {
  amount: tThresholdAmount;
  /** RFC 3339 en UTC. */
  startedAt: string;
};

/** El objeto `state` del contrato. El GET lo devuelve suelto. */
export type tThresholdTopUpState = {
  status: tThresholdTopUpStatus;
  currency: tWalletCurrency;
  threshold: tThresholdAmount | null;
  amount: tThresholdAmount | null;
  lastValues: tThresholdLastValues | null;
  limits: tThresholdLimits;
  today: { count: number };
  /** Null salvo con `status: paused`. */
  pause: tThresholdPause | null;
  chargeInProgress: tThresholdChargeInProgress | null;
  /** Solo con `status: off` apagada por el sistema (RET-2423). */
  offReason: tThresholdOffReason | null;
  /**
   * Fecha `YYYY-MM-DD` (UTC) de la cancelación programada (RET-2423). Solo con
   * `status` `active` o `paused` y en el futuro; con `off` es null.
   */
  endsOn: string | null;
  typeChange: tThresholdTypeChange;
};

/**
 * El cobro que hizo ESTE PUT. Cobrado no es acreditado: el asiento llega por el
 * webhook. `status` es un conjunto abierto (`charged`, `pending`): un valor
 * desconocido no invalida un 200, porque el 200 ya dice que se guardó.
 */
export type tThresholdCharge = {
  status: string;
  amount: tThresholdAmount;
};

/** El 200 del PUT: `{state, charge}`. */
export type tThresholdSaveResult = {
  state: tThresholdTopUpState;
  charge: tThresholdCharge | null;
};

/**
 * El `charge` del 200 del retry (`thresholdRetryChargeJSON`). `status` es
 * `charged`, `pending` o `requires_action`.
 *
 * `clientSecret` es null salvo con `requires_action`: es el secreto del
 * PaymentIntent con el que el banco pide autenticar (3DS). Es un secreto de
 * una sola función: solo se le entrega a `stripe.handleNextAction`. Nunca va a
 * un error, a `extra` de Sentry, a un log, a analítica ni a `sessionStorage`.
 */
export type tThresholdRetryCharge = tThresholdCharge & {
  clientSecret: string | null;
};

/** El 200 del retry: `{state, charge}`; aquí `charge` siempre llega. */
export type tThresholdRetryResult = {
  state: tThresholdTopUpState;
  charge: tThresholdRetryCharge;
};

/** El 200 del DELETE: `{state}`. */
export type tThresholdDisableResult = {
  state: tThresholdTopUpState;
};

/**
 * Códigos del 400 `invalid_amount` (`domain.ThresholdBound*`, contrato con el
 * front). Conjunto abierto en el error tipado: un código que no está aquí usa el
 * texto genérico.
 */
export type tThresholdBoundCode =
  | 'threshold_below_min'
  | 'amount_below_min'
  | 'amount_above_max'
  | 'amount_not_above_threshold'
  | 'not_whole_units';
